import React, { useEffect, useState, useCallback } from "react";
import { WifiOff, AlertCircle, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import {
  listPendingKeys, getPending, removePending,
  subscribePendingChanged, notifyPendingChanged,
} from "../lib/photoQueue";

/**
 * NetworkStatus — sticky banner shown at the top of the protected layout when
 * the device is offline OR when there are photos pending upload. The "Riprova"
 * button flushes the pending queue (best-effort, the form cells will update
 * their `value` via a custom event once the upload succeeds).
 *
 * Note: actually swapping the path back into the form state is handled by the
 * UploaderCell components themselves — this banner is a *global* indicator +
 * trigger only. The banner emits a `uc:retry-pending-photos` window event that
 * each mounted cell listens to.
 */
export default function NetworkStatus() {
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [pendingCount, setPendingCount] = useState(0);
  const [retrying, setRetrying] = useState(false);

  // Online/offline listener
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  // Pending-photo count, kept in sync via custom events
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      const keys = await listPendingKeys();
      if (!cancelled) setPendingCount(keys.length);
    };
    refresh();
    const unsub = subscribePendingChanged(refresh);
    return () => { cancelled = true; unsub(); };
  }, []);

  const flushAll = useCallback(async () => {
    if (retrying) return;
    setRetrying(true);
    try {
      const keys = await listPendingKeys();
      for (const k of keys) {
        const rec = await getPending(k);
        if (!rec) continue;
        try {
          const fd = new FormData();
          const f = new File([rec.blob], rec.name || "foto.jpg", { type: rec.mime || "image/jpeg" });
          fd.append("file", f);
          const { data } = await api.post("/upload", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 90000,
          });
          // Tell any mounted cell about the swap so they can update their form state
          window.dispatchEvent(new CustomEvent("uc:pending-photo-flushed", {
            detail: { key: k, newPath: data.path },
          }));
          // Update any already-persisted record (form was submitted while offline)
          try {
            await api.post("/uploads/resolve-pending", { key: k, new_path: data.path });
          } catch { /* best-effort: not critical for in-form cases */ }
          await removePending(k);
          notifyPendingChanged();
        } catch {
          // leave in queue, will retry later
        }
      }
    } finally {
      setRetrying(false);
    }
  }, [retrying]);

  // Auto-retry when we come back online
  useEffect(() => {
    if (online && pendingCount > 0) {
      // small delay so the network actually stabilizes before retrying
      const t = setTimeout(() => { flushAll(); }, 1500);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [online, pendingCount, flushAll]);

  if (online && pendingCount === 0) return null;

  return (
    <div
      className={`sticky top-16 z-30 border-b-[1.5px] ${
        !online ? "bg-[#ff3b30] text-white border-black" : "bg-[#ffcc00] text-black border-black"
      }`}
      data-testid="network-status"
    >
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center gap-3 text-sm">
        {!online ? (
          <>
            <WifiOff className="w-4 h-4 shrink-0" />
            <span className="font-bold">Sei offline.</span>
            <span className="opacity-90 hidden sm:inline">Le foto vengono salvate sul dispositivo e caricate al ritorno della connessione.</span>
          </>
        ) : (
          <>
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span className="font-bold">{pendingCount} foto in coda</span>
            <span className="opacity-80 hidden sm:inline">salvate sul dispositivo · in attesa di upload</span>
          </>
        )}
        {online && pendingCount > 0 && (
          <button
            onClick={flushAll}
            disabled={retrying}
            className="ml-auto bg-black text-white px-3 py-1 rounded-full text-xs font-bold inline-flex items-center gap-1 disabled:opacity-60"
            data-testid="network-status-retry"
          >
            {retrying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            {retrying ? "Carico…" : "Riprova ora"}
          </button>
        )}
      </div>
    </div>
  );
}
